// The SmartInventory mark: the wordmark held inside two brackets, drawn with
// CSS borders per the construction formula in
// design_handoff_smartinventory_redesign/designs/assets/logo/README.md —
//   bracket height = 2 × wordmark font-size · stroke = 13% of bracket height
//   bracket width = 2.2 × stroke · gap to wordmark = 0.5 × wordmark font-size
//   corner radius = 0.11 × bracket height (outer corners only)
// Derived from the theme's own subtitle1 size, not a retyped literal.
import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';

// `bracketColor`/`wordmarkColor`/`accentColor` default to the theme's own
// primary/text tones; Login's dark right-hand panel passes lighter violet
// overrides so the mark reads against that gradient instead of disappearing.
const BracketLogo = ({ compact = false, sx, bracketColor = 'primary.main', wordmarkColor = 'text.primary', accentColor = 'primary.main' }) => {
  const theme = useTheme();
  const wordmarkSize = theme.typography.subtitle1.fontSize;
  const bracketHeight = wordmarkSize * 2;
  const stroke = bracketHeight * 0.13;
  const bracketWidth = stroke * 2.2;
  // Compact (icon-only, no wordmark) still needs a visible gap between the
  // two brackets — reuse the same "gap to wordmark" proportion so they read
  // as a bracket pair instead of merging into one solid block.
  const gap = wordmarkSize * 0.5;
  const cornerRadius = bracketHeight * 0.11;

  const bracketSx = (side) => ({
    width: bracketWidth,
    height: bracketHeight,
    borderTop: `${stroke}px solid`,
    borderBottom: `${stroke}px solid`,
    borderColor: bracketColor,
    boxSizing: 'border-box',
    flexShrink: 0,
    ...(side === 'left'
      ? {
        borderLeft: `${stroke}px solid`,
        borderTopLeftRadius: cornerRadius,
        borderBottomLeftRadius: cornerRadius,
      }
      : {
        borderRight: `${stroke}px solid`,
        borderTopRightRadius: cornerRadius,
        borderBottomRightRadius: cornerRadius,
      }),
  });

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: `${gap}px`, ...sx }}>
      <Box sx={bracketSx('left')} />
      {!compact && (
        <Typography variant="subtitle1" component="span" noWrap sx={{ letterSpacing: '-0.02em', color: wordmarkColor }}>
          Smart<Box component="span" sx={{ color: accentColor }}>Inventory</Box>
        </Typography>
      )}
      <Box sx={bracketSx('right')} />
    </Box>
  );
};

export default BracketLogo;
